import classnames from 'classnames'
import styles from './MovingContainer.module.scss'
import gsap from 'gsap'
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react'
import useWindowResize from '@/hooks/use-window-resize'
import useStore from '@/store'
import useBreakpoint from '@/hooks/use-breakpoint'

const MovingContainer = forwardRef(({ className, disable, strengthMultiplier = 1, children }, ref) => {
  const $containerRef = useRef()
  const resizeKey = useWindowResize()
  const unsubRef = useRef()
  const position = useRef({
    top: null,
    left: null,
  })
  const moveFunctions = useRef(null)
  const overallMultiplier = useRef(1)
  const { isMobile } = useBreakpoint()

  useEffect(() => {
    if (!$containerRef.current) return

    if (isMobile) {
      gsap.set($containerRef.current, {
        clearProps: 'x,y',
      })
      return
    }

    const rect = $containerRef.current.getBoundingClientRect()
    const height = $containerRef.current.offsetHeight
    const width = $containerRef.current.offsetWidth
    position.current = {
      top: rect.top + height * 0.5,
      left: rect.left + width * 0.5,
    }

    const xTo = gsap.quickTo($containerRef.current, 'x', { duration: 0.6, ease: 'power3' })
    const yTo = gsap.quickTo($containerRef.current, 'y', { duration: 0.6, ease: 'power3' })

    moveFunctions.current = {}
    moveFunctions.current.x = xTo
    moveFunctions.current.y = yTo
  }, [resizeKey, isMobile])

  useEffect(() => {
    if (unsubRef.current) unsubRef.current()

    if (disable || isMobile) return

    unsubRef.current = useStore.subscribe(
      state => [state.cursorPosition],
      value => {
        if (!moveFunctions.current) return
        const strength = 0.05
        const fromTop = (position.current.top - value[0].y) * strength * strengthMultiplier * overallMultiplier.current
        const fromLeft =
          (position.current.left - value[0].x) * strength * strengthMultiplier * overallMultiplier.current
        moveFunctions.current.x(fromLeft)
        moveFunctions.current.y(fromTop)
      },
    )

    return () => {
      if (unsubRef.current) unsubRef.current()
    }
  }, [disable, strengthMultiplier, isMobile])

  useImperativeHandle(ref, () => ({
    overallMultiplier,
  }))

  return (
    <div
      ref={$containerRef}
      className={classnames(styles.MovingContainer, className)}
    >
      {children}
    </div>
  )
})

MovingContainer.displayName = 'MovingContainer'

export default MovingContainer
